import { getAuth } from 'firebase/auth';
import { API_CONFIG, getApiUrl } from '../config/api';
import { handleFetchApiError } from './apiErrorHandler';
import { CloudAccessError, parseCloudErrorPayload } from './cloudErrorGuide';

export type MediaCategory = 'audio' | 'artwork' | 'promo' | 'video' | 'footage';

export type MediaAccessResult = {
  url: string;
  expiresAt?: string;
  mode: 'presigned' | 'blob';
  revoke?: () => void;
};

function legacyMediaEndpoint(trackId: string, fileId: string, category: MediaCategory): string {
  switch (category) {
    case 'audio':
      return API_CONFIG.ENDPOINTS.STREAM_AUDIO(trackId, fileId);
    case 'artwork':
      return API_CONFIG.ENDPOINTS.PREVIEW_ARTWORK(trackId, fileId);
    case 'promo':
      return API_CONFIG.ENDPOINTS.PREVIEW_PROMO(trackId, fileId);
    case 'video':
      return API_CONFIG.ENDPOINTS.STREAM_VIDEO(trackId, fileId);
    case 'footage':
      return API_CONFIG.ENDPOINTS.STREAM_FOOTAGE(trackId, fileId);
    default:
      throw new Error(`Unsupported media category: ${category}`);
  }
}

async function fetchLegacyBlobUrl(trackId: string, fileId: string, category: MediaCategory): Promise<MediaAccessResult> {
  const user = getAuth().currentUser;
  if (!user) {
    throw new Error('No user');
  }
  const token = await user.getIdToken();
  const endpoint = legacyMediaEndpoint(trackId, fileId, category);
  const res = await fetch(getApiUrl(endpoint), {
    credentials: 'include',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let data: Record<string, unknown> | undefined;
    try {
      data = (await res.clone().json()) as Record<string, unknown>;
    } catch {
      data = undefined;
    }
    await handleFetchApiError(res);
    const payload = parseCloudErrorPayload(data, res.status);
    if (payload) {
      throw new CloudAccessError(payload);
    }
    throw new Error(`Failed to fetch media (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  return {
    url,
    mode: 'blob',
    revoke: () => URL.revokeObjectURL(url),
  };
}

export function isCassetteCloudStorage(storageProvider?: string): boolean {
  return storageProvider === 'cassette_s3';
}

function looksLikeCassetteCloudKey(fileId: string): boolean {
  return fileId.startsWith('users/');
}

async function materializePresignedBlob(presignedUrl: string): Promise<MediaAccessResult> {
  const res = await fetch(presignedUrl);
  if (!res.ok) {
    throw new Error(`Failed to fetch media from storage (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  return {
    url,
    mode: 'blob',
    revoke: () => URL.revokeObjectURL(url),
  };
}

async function mintPresignedAccess(
  trackId: string,
  fileId: string,
  category: MediaCategory,
): Promise<MediaAccessResult> {
  const user = getAuth().currentUser;
  if (!user) {
    throw new Error('No user');
  }
  const token = await user.getIdToken();
  const endpoint = API_CONFIG.ENDPOINTS.FILE_ACCESS(trackId, fileId, category);
  const res = await fetch(getApiUrl(endpoint), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let data: Record<string, unknown> | undefined;
    try {
      data = (await res.clone().json()) as Record<string, unknown>;
    } catch {
      data = undefined;
    }
    await handleFetchApiError(res);
    const payload = parseCloudErrorPayload(data, res.status);
    if (payload) {
      throw new CloudAccessError(payload);
    }
    throw new Error(`Failed to mint media access URL (${res.status})`);
  }
  const data = await res.json() as { url: string; expiresAt?: string; mode?: string };
  if (!data.url) {
    throw new Error('Missing presigned URL');
  }
  // Audio/waveform needs a same-origin blob URL — cross-origin S3 breaks WaveSurfer decode.
  if (category === 'audio') {
    const blobAccess = await materializePresignedBlob(data.url);
    return { ...blobAccess, expiresAt: data.expiresAt };
  }
  return {
    url: data.url,
    expiresAt: data.expiresAt,
    mode: 'presigned',
  };
}

export async function resolveTrackMediaUrl(
  trackId: string,
  fileId: string,
  category: MediaCategory,
  storageProvider?: string,
): Promise<MediaAccessResult> {
  // Cassette Cloud audio is proxied through the API (same-origin blob) because R2 has no
  // browser CORS and WaveSurfer cannot decode cross-origin presigned URLs.
  if (
    category === 'audio'
    && (isCassetteCloudStorage(storageProvider) || looksLikeCassetteCloudKey(fileId))
  ) {
    return fetchLegacyBlobUrl(trackId, fileId, category);
  }

  if (isCassetteCloudStorage(storageProvider) || looksLikeCassetteCloudKey(fileId)) {
    return mintPresignedAccess(trackId, fileId, category);
  }

  try {
    return await fetchLegacyBlobUrl(trackId, fileId, category);
  } catch (error) {
    if (looksLikeCassetteCloudKey(fileId)) {
      return mintPresignedAccess(trackId, fileId, category);
    }
    throw error;
  }
}
